import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send, Save, Calendar, Image as ImageIcon, Trash2, Edit } from 'lucide-react';

import { API_URL } from '../config';
const API = API_URL;

const MobileAdminPost = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('create');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    image_url: '',
    image_file: null,
    mega_folder_link: '',
    linkvertise_url: '',
    premium_key_link: '',
    post_type: 'free',
    status: 'published',
    scheduled_at: '',
    feed_id: 'home'
  });
  const [editingPost, setEditingPost] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchFeeds();
  }, []);

  useEffect(() => {
    if (activeTab !== 'create') {
      fetchPosts();
    }
  }, [activeTab]);

  const fetchFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/feeds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeds(response.data.feeds || []);
    } catch (error) {
      console.error('Failed to load feeds:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const statusMap = {
        'drafts': 'draft',
        'scheduled': 'scheduled',
        'published': 'published'
      };
      const status = statusMap[activeTab];
      
      if (status) {
        const response = await axios.get(`${API}/posts?status=${status}&limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPosts(response.data);
      }
    } catch (error) {
      console.error('Failed to load posts');
    }
  };

  const handleSubmit = async (action) => {
    if (!formData.text.trim()) {
      toast.error('Description is required');
      return;
    }

    setLoading(true);
    const submitData = { ...formData };
    
    // Handle image upload
    if (formData.image_file) {
      const reader = new FileReader();
      submitData.image_url = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(formData.image_file);
      });
    }
    submitData.image_file = undefined;
    
    if (action === 'publish') {
      submitData.status = 'published';
      submitData.scheduled_at = null;
    } else if (action === 'draft') {
      submitData.status = 'draft';
      submitData.scheduled_at = null;
    } else if (action === 'schedule') {
      if (!submitData.scheduled_at) {
        toast.error('Please select a date and time');
        setLoading(false);
        return;
      }
      submitData.status = 'scheduled';
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.post_id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/posts`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(action === 'publish' ? 'Post published!' : action === 'draft' ? 'Saved as draft' : 'Post scheduled');
      }

      setFormData({
        title: '',
        text: '',
        image_url: '',
        image_file: null,
        mega_folder_link: '',
        linkvertise_url: '',
        premium_key_link: '',
        post_type: 'free',
        status: 'published',
        scheduled_at: '',
        feed_id: 'home'
      });
      setImagePreview(null);
      setEditingPost(null);
      setActiveTab('published');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }

    setLoading(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      setFormData({ ...formData, image_file: file, image_url: '' });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title || '',
      text: post.text,
      image_url: post.image_url || '',
      mega_folder_link: post.mega_folder_link || '',
      linkvertise_url: post.linkvertise_url || '',
      premium_key_link: post.premium_key_link || '',
      post_type: post.post_type,
      status: post.status,
      scheduled_at: post.scheduled_at || '',
      feed_id: post.feed_id || 'home'
    });
    setImagePreview(post.image_url || null);
    setEditingPost(post);
    setActiveTab('create');
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <header
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{ background: 'rgba(0, 0, 0, 0.95)', borderBottom: '1px solid #2A2A2A' }}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <Button
              onClick={() => window.location.href = user?.role === 'super_admin' ? '/admin' : '/feed'}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-black" style={{ color: '#EE1C4E' }}>
              {editingPost ? 'Edit Post' : 'New Post'}
            </h1>
            <div className="w-8" />
          </div>

          <div className="flex gap-2 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            {['create', 'drafts', 'scheduled', 'published'].map((tab) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setEditingPost(null); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all"
                style={{
                  background: activeTab === tab ? '#EE1C4E' : '#1A1A1A',
                  color: 'white'
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="px-4 py-6 pb-24">
        {activeTab === 'create' ? (
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300 text-base mb-2 block">Feed *</Label>
              <Select
                value={formData.feed_id}
                onValueChange={(value) => setFormData({ ...formData, feed_id: value })}
              >
                <SelectTrigger className="h-12 bg-black/40 border-gray-700 text-white text-base" data-testid="mobile-feed-selector">
                  <SelectValue placeholder="Select feed" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-gray-700">
                  {feeds.map((feed) => (
                    <SelectItem 
                      key={feed.feed_id} 
                      value={feed.feed_id}
                      className="text-white hover:bg-[#2A2A2A] text-base"
                    >
                      {feed.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Choose which feed page this post appears on</p>
            </div>
            
            <div>
              <Label className="text-gray-300 text-base mb-2 block">Image</Label>
              <div className="space-y-3">
                <div className="relative">
                  <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value, image_file: null })}
                    placeholder="Image URL (https://...)"
                    className="pl-11 h-12 bg-black/40 border-gray-700 text-white text-base"
                  />
                </div>
                <div className="text-center text-gray-500 text-sm">OR</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700"
                  data-testid="mobile-image-file-input"
                />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-2 w-full h-48 object-cover rounded-lg" />
                )}
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-base mb-2 block">Description *</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Write your post description..."
                rows={6}
                className="bg-black/40 border-gray-700 text-white text-base resize-none"
                style={{ fontSize: '16px' }}
              />
            </div>

            <div>
              <Label className="text-gray-300 text-base mb-2 block">Mega Folder Link</Label>
              <Input
                value={formData.mega_folder_link}
                onChange={(e) => setFormData({ ...formData, mega_folder_link: e.target.value })}
                placeholder="https://example.com/download..."
                className="h-12 bg-black/40 border-gray-700 text-white text-base"
              />
            </div>

            <div>
              <Label className="text-gray-300 text-base mb-2 block">Linkvertise Link (free users)</Label>
              <Input
                value={formData.linkvertise_url}
                onChange={(e) => setFormData({ ...formData, linkvertise_url: e.target.value })}
                placeholder="https://example.com/unlock..."
                className="h-12 bg-black/40 border-gray-700 text-white text-base"
              />
            </div>

            <div>
              <Label className="text-gray-300 text-base mb-2 block">Premium Key Link</Label>
              <Input
                value={formData.premium_key_link}
                onChange={(e) => setFormData({ ...formData, premium_key_link: e.target.value })}
                placeholder="https://example.com/key..."
                className="h-12 bg-black/40 border-gray-700 text-white text-base"
              />
            </div>

            <div>
              <Label className="text-gray-300 text-base mb-2 block">Post Type</Label>
              <Select value={formData.post_type} onValueChange={(value) => setFormData({ ...formData, post_type: value })}>
                <SelectTrigger className="h-12 bg-black/40 border-gray-700 text-white text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}>
                  <SelectItem value="free" className="text-white">Free</SelectItem>
                  <SelectItem value="premium" className="text-white">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-300 text-base mb-2 block">Schedule (Optional)</Label>
              <Input
                type="datetime-local"
                value={formData.scheduled_at ? formData.scheduled_at.slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="h-12 bg-black/40 border-gray-700 text-white text-base"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.length === 0 ? (
              <p className="text-center text-gray-500 py-12">No {activeTab} posts</p>
            ) : (
              posts.map((post) => (
                <div
                  key={post.post_id}
                  className="p-4 rounded-xl"
                  style={{ background: '#121212', border: '1px solid #2A2A2A' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-white font-bold text-base">{post.title || 'Untitled'}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-2 text-gray-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.post_id)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm line-clamp-2">{post.text}</p>
                  <p className="text-gray-600 text-xs mt-2">
                    {new Date(post.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {activeTab === 'create' && (
        <div
          className="fixed bottom-0 left-0 right-0 p-4 backdrop-blur-xl"
          style={{ background: 'rgba(0, 0, 0, 0.95)', borderTop: '1px solid #2A2A2A' }}
        >
          <div className="flex gap-3">
            <Button
              onClick={() => handleSubmit('publish')}
              disabled={loading}
              className="flex-1 h-14 text-white font-bold rounded-full flex items-center justify-center gap-2 text-base"
              style={{ background: '#EE1C4E' }}
            >
              <Send className="w-5 h-5" />
              {loading ? 'Publishing...' : editingPost ? 'Update' : 'Publish'}
            </Button>
            
            <Button
              onClick={() => handleSubmit('draft')}
              disabled={loading}
              variant="outline"
              className="h-14 px-6 rounded-full border-gray-700 text-gray-300"
            >
              <Save className="w-5 h-5" />
            </Button>
            
            {formData.scheduled_at && (
              <Button
                onClick={() => handleSubmit('schedule')}
                disabled={loading}
                variant="outline"
                className="h-14 px-6 rounded-full border-gray-700 text-gray-300"
              >
                <Calendar className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAdminPost;
